import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { extensionDownloadUrl, getExtensionVersion } from "../../api";
import { Badge, I } from "../../ui";
import { useDemoScope } from "../../useDemoScope";
import extensionsGuideImage from "../../assets/legacy-extensions-guide.svg";
import folderGuideImage from "../../assets/legacy-folder-guide.svg";
import pinGuideImage from "../../assets/legacy-pin-guide.svg";
import pairingCodeImage from "../../assets/legacy-pairing-code.png";
import pairingPopupImage from "../../assets/legacy-pairing-popup.png";
import { FOLDER_NAME, INSTALL_COPY, UPDATE_COPY } from "./install-copy";

function Mono({ children }: { children: ReactNode }) {
  return <code className="install-code">{children}</code>;
}

function InstallStep({ title, result, children }: { title: string; result?: ReactNode; children: ReactNode }) {
  return (
    <li>
      <strong>{title}</strong>
      <div className="install-step-body">
        {children}
        {result && <div className="install-result"><strong>Controlla:</strong> {result}</div>}
      </div>
    </li>
  );
}

export function ExtensionInstall({ onOpenPairing, version: providedVersion, versionErr: providedVersionErr = false, updateGuideOpen = false }: {
  onOpenPairing: () => void;
  version?: string | null;
  versionErr?: boolean;
  updateGuideOpen?: boolean;
}) {
  const demoScope = useDemoScope();
  const [fetchedVersion, setFetchedVersion] = useState<string | null>(null);
  const [fetchErr, setFetchErr] = useState(false);
  useEffect(() => {
    if (providedVersion !== undefined) return;
    let cancelled = false;
    getExtensionVersion()
      .then(({ version }) => { if (!cancelled) setFetchedVersion(version); })
      .catch(() => { if (!cancelled) setFetchErr(true); });
    return () => { cancelled = true; };
  }, [providedVersion]);
  const version = providedVersion === undefined ? fetchedVersion : providedVersion;
  const versionErr = providedVersion === undefined ? fetchErr : providedVersionErr;

  const versionLabel = version ? `v${version}` : versionErr ? "non disponibile" : "…";

  return (
    <div className="col gap-14 install-guide-content">
      <div className="install-intro">
        <div>
          <strong>Prima installazione</strong>
          <p>Usa Chrome o Edge sul computer con cui accederai a SISTER. Segui i passi nell'ordine.</p>
        </div>
        <Badge kind={version ? "info" : "muted"}>Versione {versionLabel}</Badge>
      </div>

      <ol className="install-steps">
        <InstallStep title="Scarica l'estensione" result="Trovi un file .zip nella cartella Download.">
          <p>Scarica il file su questo computer.</p>
          {demoScope ? (
            <button type="button" className="btn primary" disabled>{I.download} Download non disponibile nella demo</button>
          ) : (
            <a className="btn primary" href={extensionDownloadUrl()} download>
              {I.download} {version ? `Scarica Legacy ${versionLabel} (.zip)` : "Scarica Legacy (.zip)"}
            </a>
          )}
        </InstallStep>

        <InstallStep title="Estrai il file ZIP">
          <p>{INSTALL_COPY.windowsExtract}</p>
          <p>{INSTALL_COPY.macExtract}</p>
        </InstallStep>

        <InstallStep title="Metti la cartella in Documenti" result={<>Aprendo <Mono>{FOLDER_NAME}</Mono> trovi il file <Mono>manifest.json</Mono>.</>}>
          <p>Apri la cartella appena estratta. {INSTALL_COPY.doNotMoveFolder}</p>
          <figure className="install-guide-image">
            <a href={folderGuideImage} target="_blank" rel="noreferrer" aria-label="Ingrandisci l'esempio della cartella da caricare">
              <img src={folderGuideImage} alt="Esempio visivo: estrai lo ZIP e sposta in Documenti la cartella sister-session-helper che contiene manifest.json" loading="lazy" />
            </a>
            <figcaption>La cartella da caricare è <Mono>{FOLDER_NAME}</Mono>, non il file ZIP. Premi l'immagine per ingrandirla.</figcaption>
          </figure>
        </InstallStep>

        <InstallStep title="Apri la pagina delle estensioni">
          <p>{INSTALL_COPY.chromeExtensions} {INSTALL_COPY.edgeExtensions}</p>
          <div className="install-addresses" aria-label="Indirizzo della pagina estensioni">
            <span>Chrome <Mono>chrome://extensions</Mono></span>
            <span>Edge <Mono>edge://extensions</Mono></span>
          </div>
        </InstallStep>

        <InstallStep title="Attiva Modalità sviluppatore">
          <p>{INSTALL_COPY.developerMode} Su Edge, l'interruttore è nel menu a sinistra.</p>
          <p>Vedrai comparire il pulsante <strong>Carica estensione non pacchettizzata</strong>.</p>
        </InstallStep>

        <InstallStep title="Carica la cartella" result="Nella pagina delle estensioni compare una scheda chiamata Legacy.">
          <p>{INSTALL_COPY.loadUnpacked}</p>
          <div className="install-folder-choice">
            <span>Cartella da selezionare in Documenti:</span><strong>{FOLDER_NAME}</strong>
          </div>
          <figure className="install-guide-image">
            <a href={extensionsGuideImage} target="_blank" rel="noreferrer" aria-label="Ingrandisci l'esempio della pagina Estensioni di Chrome">
              <img src={extensionsGuideImage} alt="Esempio visivo di Chrome: pagina Estensioni, Modalità sviluppatore, pulsante Carica estensione non pacchettizzata e scheda Legacy" loading="lazy" />
            </a>
            <figcaption>Esempio visivo in Chrome. Su Edge usa <Mono>edge://extensions</Mono>. Premi l'immagine per ingrandirla.</figcaption>
          </figure>
        </InstallStep>

        <InstallStep title="Fissa l'icona Legacy" result="L'icona Legacy è visibile accanto alla barra degli indirizzi.">
          <p>In <strong>Chrome</strong> premi l'icona a puzzle in alto a destra. Trova <strong>Legacy</strong> e premi la puntina <strong>Fissa</strong>.</p>
          <p>In <strong>Edge</strong> apri il menu Estensioni e, accanto a <strong>Legacy</strong>, premi l'icona a occhio <strong>Mostra nella barra degli strumenti</strong>.</p>
          <figure className="install-guide-image">
            <a href={pinGuideImage} target="_blank" rel="noreferrer" aria-label="Ingrandisci l'esempio per fissare l'icona Legacy">
              <img src={pinGuideImage} alt="Esempio visivo di Chrome: nel menu Estensioni premi la puntina accanto a Legacy, poi l'icona compare nella barra" loading="lazy" />
            </a>
            <figcaption>Esempio visivo in Chrome: fissa Legacy e aprila dalla barra. Premi l'immagine per ingrandirla.</figcaption>
          </figure>
        </InstallStep>

        <InstallStep title="Genera il codice di abbinamento" result="Compare un codice da inserire nel popup Legacy.">
          <p>Apri <strong>Nuovo abbinamento</strong> nella pagina Estensione Chrome e premi <strong>Genera codice</strong>. Il codice dura 5 minuti: generarlo adesso ti lascia il tempo per inserirlo.</p>
          {!demoScope && (
            <button type="button" className="btn primary" onClick={onOpenPairing}>
              {I.ext} Apri Nuovo abbinamento
            </button>
          )}
          <figure className="install-guide-image install-pairing-code-shot">
            <a href={pairingCodeImage} target="_blank" rel="noreferrer" aria-label="Apri l'esempio della finestra Codice abbinamento a grandezza piena">
              <img src={pairingCodeImage} alt="Finestra Codice abbinamento con il codice da copiare e le istruzioni per inserirlo nel popup Legacy" loading="lazy" />
            </a>
            <figcaption>Esempio visivo: il codice nell'immagine è solo dimostrativo. Genera il tuo con il pulsante qui sopra.</figcaption>
          </figure>
        </InstallStep>

        <InstallStep title="Collega il dispositivo" result="Il computer appare tra le Estensioni abbinate.">
          <p>Apri l'icona <strong>Legacy</strong>. Lascia l'indirizzo della console già compilato, incolla il codice nel campo <strong>Codice di collegamento</strong> e premi <strong>Collega dispositivo</strong>.</p>
          <p>Torna alla console e chiudi la finestra del codice per vedere il computer nella lista.</p>
          <p>Se il codice è scaduto, premi <strong>Genera nuovo codice</strong> nella stessa finestra.</p>
          <figure className="install-popup-shot">
            <a href={pairingPopupImage} target="_blank" rel="noreferrer" aria-label="Apri la schermata del popup Legacy a grandezza piena">
              <img src={pairingPopupImage} alt="Popup Legacy con il campo Codice di collegamento e il pulsante Collega dispositivo" loading="lazy" />
            </a>
            <figcaption>Il popup prima del collegamento. La versione mostrata può cambiare.</figcaption>
          </figure>
        </InstallStep>
      </ol>

      <details id="aggiornare-estensione" className="install-update" open={updateGuideOpen}>
        <summary>Hai già Legacy? Istruzioni per aggiornare</summary>
        <ol>
          <li>{UPDATE_COPY.alreadyInstalled}</li>
          <li>{UPDATE_COPY.reload}</li>
          <li>Apri il popup Legacy e controlla che la versione in alto a destra sia {version ? versionLabel : "uguale a quella indicata in questa pagina"}.</li>
        </ol>
        <p>{UPDATE_COPY.noAutoUpdate}</p>
      </details>
    </div>
  );
}
